import { Component, inject, OnInit, OnDestroy, effect } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButtonToggleModule, MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';

import { Provision } from '../../objects/provision';
import { ProvisionsService } from '../../service/entity-services/provisions.service';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';

/**
 * My Work → Provisions.
 *
 * Deliberately minimal: the provisions cache is already loaded app-wide, so this
 * just filters it to the signed-in user and lists the result. Owner is the only
 * filter — Provision.owner is a single email string, unlike the multi-role
 * ownership on Contract/COI/BAA — so there is nothing else to offer yet.
 */
@Component({
  selector: 'app-provision-list',
  templateUrl: './provision-list.component.html',
  styleUrls: ['./provision-list.component.css'],
  standalone: true,
  imports: [CommonModule, MatProgressBar, MatButtonToggleModule, MatTableModule, MatIconModule]
})
export class ProvisionListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private provisionsService = inject(ProvisionsService);
  public filterService = inject(MyWorkFilterService);

  private destroy$ = new Subject<void>();

  MyWorkFilter = MyWorkFilter;

  allProvisions: Provision[] = [];
  filteredProvisions: Provision[] = [];
  cacheLoaded = false;

  displayedColumns = ['provName', 'contract', 'requiredAction', 'actionDate', 'status'];

  constructor() {
    // Re-filter whenever the user toggles a filter or their email resolves.
    effect(() => {
      this.filterService.activeFilters();
      this.filterService.currentUserEmail();
      this.applyFilter();
    });
  }

  ngOnInit(): void {
    // Default to Owner so the list isn't empty on arrival (every filterX method
    // in MyWorkFilterService returns [] when no filter is active).
    this.filterService.clearFilters();
    this.filterService.addFilter(MyWorkFilter.Owner);

    this.provisionsService.isCacheRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loaded => { this.cacheLoaded = loaded; });

    this.provisionsService.allProvisions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(provisions => {
        this.allProvisions = provisions || [];
        this.applyFilter();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyFilter(): void {
    this.filteredProvisions = this.filterService.filterProvisions(this.allProvisions);
  }

  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.activeFilters().includes(filter);
  }

  userChangedFilter(event: MatButtonToggleChange): void {
    this.filterService.clearFilters();
    (event.value as string[]).forEach(v => this.filterService.addFilter(v as MyWorkFilter));
  }

  openProvision(provision: Provision): void {
    if (provision.provNum) {
      this.router.navigate(['/securehome/provision', provision.provNum]);
    }
  }
}
